import { DOCUMENT } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Inject,
    OnInit,
    QueryList,
    ViewChildren,
} from "@angular/core";
import { combineLatest, map } from "rxjs";

import { VtoProduct } from "@hermes/api-model-vto";
import {
    LocalizedVtoCategory,
    LocalizedVtoSubcategory,
    VtoBeautyFacade,
} from "@hermes/states/vto-beauty";
import { DOWN, LEFT, RIGHT, UP } from "@hermes/utils-generic/constants";

@Component({
    selector: "h-virtual-try-on-selector",
    templateUrl: "./virtual-try-on-selector.component.html",
    styleUrls: ["./virtual-try-on-selector.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualTryOnSelectorComponent implements OnInit {
    @ViewChildren("categoryButtons")
    public categoryButtons!: QueryList<ElementRef>;

    @ViewChildren("subcategoryButtons")
    public subcategoryButtons!: QueryList<ElementRef>;

    /**
     * Get the focusable categories
     *
     * focusable if it's the selected subcategory
     */
    public focusableSubcategories$ = combineLatest([
        this.vtoBeautyFacade.activeCategory$,
        this.vtoBeautyFacade.activeSubcategory$,
        this.vtoBeautyFacade.vtoProductSelected$,
    ]).pipe(
        map((data) => {
            const [activeCategory, activeSubcategory]: [
                LocalizedVtoCategory,
                LocalizedVtoSubcategory,
                VtoProduct | undefined,
            ] = data;

            return activeCategory.vtoSubcategories.filter(
                (vtoSubcategory) => vtoSubcategory === activeSubcategory,
            );
        }),
    );

    constructor(
        public vtoBeautyFacade: VtoBeautyFacade,
        @Inject(DOCUMENT) public document: Document,
    ) {}

    public ngOnInit() {
        this.vtoBeautyFacade.fetchLocalizedVtoCategories();
    }

    public selectVtoCategory(vtoCategory: LocalizedVtoCategory): void {
        this.vtoBeautyFacade.updateActiveCategory(vtoCategory);
    }

    public onCategoryFocus($event: FocusEvent): void {
        ($event.target as HTMLElement)?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
        });
    }

    public selectVtoSubcategory(vtoSubcategory: LocalizedVtoSubcategory): void {
        this.vtoBeautyFacade.updateActiveSubcategory(vtoSubcategory);
    }

    public selectVtoVariant(productSku: string): void {
        this.vtoBeautyFacade.updateSelectedSkuVariantOnDisplay(productSku);
    }

    public focusAndClick(
        index: number,
        type: "category" | "subcategory",
    ): void {
        const element: Record<typeof type, ElementRef | undefined> = {
            ["category"]: this.categoryButtons.get(index),
            ["subcategory"]: this.subcategoryButtons.get(index),
        };
        if (element[type]?.nativeElement !== this.document.activeElement) {
            element[type]?.nativeElement.focus();
            element[type]?.nativeElement.click();
        }
    }

    public keydownCategory(
        event: KeyboardEvent,
        index: number,
        length: number,
        type: "category" | "subcategory",
    ): void {
        if ([LEFT, UP].includes(event.key)) {
            this.focusAndClick(index === 0 ? length : index - 1, type);
        } else if ([RIGHT, DOWN].includes(event.key)) {
            this.focusAndClick(index === length ? 0 : index + 1, type);
        }
    }

    /**
     * learnMoreRedirect
     */
    public learnMoreClick(
        selectedVtoCategory: LocalizedVtoCategory,
        selectedVtoSubcategory: LocalizedVtoSubcategory,
    ) {
        this.vtoBeautyFacade.learnMoreRedirect(
            selectedVtoCategory,
            selectedVtoSubcategory,
        );
    }
}
